import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  viewChild,
} from '@angular/core';
import { getIconSize, neverError } from '@iac-helpers';
import { IconsRegistry } from '@iac-services';
import { IacIcon, IacIconSize } from '@iac-types';

@Component({
  selector: 'iac-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class IconComponent {
  public readonly size = input<IacIconSize>();

  private renderer = inject(Renderer2);
  private iconRegistry = inject(IconsRegistry);
  private document = inject<Document>(DOCUMENT);

  private svgIcon?: SVGElement;

  public readonly iconContainer = viewChild<ElementRef<HTMLElement>>('iconContainer');

  public readonly name = input.required<IacIcon>();

  constructor() {
    effect(() => {
      const iconName = this.name();
      const svgData = this.iconRegistry.getIcon(iconName);
      if (!svgData) {
        return;
      }

      this.replaceIcon(svgData);
    });
  }

  private replaceIcon(svgContent: string): void {
    const iconContainer = this.iconContainer();
    if (this.svgIcon && iconContainer) {
      this.renderer.removeChild(iconContainer.nativeElement, this.svgIcon);
    }
    this.svgIcon = this.svgElementFromString(svgContent);
    if (iconContainer && this.svgIcon) {
      this.renderer.appendChild(iconContainer.nativeElement, this.svgIcon);
    }
  }

  private svgElementFromString(svgContent: string): SVGElement {
    const div = this.document.createElement('div');
    div.innerHTML = svgContent;
    return (
      div.querySelector('svg') ||
      this.document.createElementNS('http://www.w3.org/2000/svg', 'path')
    );
  }

  public readonly getIconSizeClasses = computed(() => {
    const size = this.size() ?? getIconSize(this.name());

    switch (size) {
      case 16:
        return ['w-4', 'h-4'];
      case 18:
        return ['w-4.5', 'h-4.5'];
      case 20:
        return ['w-5', 'h-5'];
      case 24:
        return ['w-6', 'h-6'];
      default:
        throw neverError(size);
    }
  });
}
